import React from 'react';
import { useMotion, MOTION_DEFAULTS } from './MotionContext';

const ANIM_TYPES = ['fade', 'slideUp', 'slideDown', 'slideLeft', 'slideRight', 'scale', 'blur'];
const EASINGS = ['ease', 'ease-emphasized', 'linear', 'bounce', 'elastic'];
const GROUPS = ['all', 'header', 'section', 'cards', 'pinned', 'invoices', 'clients', 'items', 'quotes'];

const IS_DEV = process.env.NODE_ENV === 'development';
const LS_KEY = 'todue.motion.panel';

// Aktivácia: ?motion=1 v URL → uloží do localStorage (panel viditeľný kým ?motion=0).
// ?motion=0 → odstráni z localStorage (panel skrytý).
function isVisible() {
  if (!IS_DEV || typeof window === 'undefined') return false;
  const param = new URLSearchParams(window.location.search).get('motion');
  if (param === '1') {
    try { localStorage.setItem(LS_KEY, '1'); } catch {}
    return true;
  }
  if (param === '0') {
    try { localStorage.removeItem(LS_KEY); } catch {}
    return false;
  }
  try { return localStorage.getItem(LS_KEY) === '1'; } catch { return false; }
}

/**
 * Floating panel pre experimentovanie s motion parametrami.
 * Viditeľný IBA v dev mode + ?motion=1 URL param.
 * V produkčnom buildi sa nezobrazí.
 */
export function MotionPanel() {
  const { config, update, replay, reset } = useMotion();
  if (!isVisible()) return null;

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <span style={{ fontWeight: 700 }}>Motion tweaks</span>
        <button onClick={reset} style={resetBtnStyle} title="Reset to defaults">↺</button>
      </div>

      <Row label="Anim in">
        <select value={config.animIn} onChange={(e) => update('animIn', e.target.value)} style={selectStyle}>
          {ANIM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Row>

      <Row label={`Duration · ${config.duration}ms`}>
        <input type="range" min={0} max={2000} step={50}
          value={config.duration}
          onChange={(e) => update('duration', Number(e.target.value))}
          style={rangeStyle} />
      </Row>

      <Row label={`Stagger · ${config.stagger}ms`}>
        <input type="range" min={0} max={300} step={10}
          value={config.stagger}
          onChange={(e) => update('stagger', Number(e.target.value))}
          style={rangeStyle} />
      </Row>

      <Row label={`Distance · ${config.distance}px`}>
        <input type="range" min={0} max={120} step={2}
          value={config.distance}
          onChange={(e) => update('distance', Number(e.target.value))}
          style={rangeStyle} />
      </Row>

      <Row label="Easing">
        <select value={config.easing} onChange={(e) => update('easing', e.target.value)} style={selectStyle}>
          {EASINGS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Row>

      <Row label="Apply to group">
        <select value={config.group} onChange={(e) => update('group', e.target.value)} style={selectStyle}>
          {GROUPS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Row>

      <button onClick={replay} style={replayBtnStyle}>↻ Replay</button>

      <details style={{ marginTop: 10, fontSize: 10, color: 'var(--text-muted)' }}>
        <summary style={{ cursor: 'pointer' }}>Defaults na zapísanie</summary>
        <pre style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>
{JSON.stringify(config, null, 2)}
        </pre>
        <span>Skopíruj do `MOTION_DEFAULTS` v `MotionContext.jsx`.</span>
      </details>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </div>
  );
}

// ─── Styles ───
const panelStyle = {
  position: 'fixed', bottom: 100, right: 16, zIndex: 1000,
  background: 'var(--bg-surface)', border: '1px solid var(--border-strong)',
  borderRadius: 'var(--r-lg)', padding: '12px 14px',
  width: 240, fontFamily: 'var(--font)', fontSize: 12,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
  color: 'var(--text-primary)',
};
const headerStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 12, paddingBottom: 8,
  borderBottom: '1px solid var(--border)',
};
const labelStyle = {
  color: 'var(--text-muted)', fontSize: 10,
  textTransform: 'uppercase', letterSpacing: '0.05em',
};
const selectStyle = {
  width: '100%', padding: '4px 6px',
  background: 'var(--bg-input)', border: '1px solid var(--border)',
  borderRadius: 'var(--r-sm)', color: 'var(--text-primary)',
  fontFamily: 'inherit', fontSize: 12,
};
const rangeStyle = { width: '100%' };
const resetBtnStyle = {
  background: 'transparent', border: '1px solid var(--border)',
  borderRadius: 'var(--r-sm)', padding: '2px 6px',
  cursor: 'pointer', fontSize: 11, color: 'var(--text-secondary)',
};
const replayBtnStyle = {
  marginTop: 4, width: '100%', padding: '6px 12px',
  background: 'var(--brand-green)', color: '#fff', border: 'none',
  borderRadius: 'var(--r-full)', fontWeight: 600, cursor: 'pointer',
  fontFamily: 'inherit', fontSize: 12,
};
